
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/icons/PlaybackIcons';
import SEO from '../components/SEO';

const LegalLayout: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-8 overflow-y-auto">
    <SEO title={`${title} | RaceLineIQ`} />
    <div className="max-w-4xl mx-auto bg-gray-800 rounded-lg shadow-xl p-6 md:p-10 border border-gray-700">
      <Link to="/" className="inline-flex items-center text-cyan-400 hover:text-cyan-300 mb-6 transition-colors">
        <ArrowLeftIcon className="w-4 h-4 mr-2" />
        Back
      </Link>
      <h1 className="text-3xl font-bold mb-6 text-white border-b border-gray-700 pb-4">{title}</h1>
      <div className="prose prose-invert max-w-none text-gray-300 space-y-4">
        {children}
      </div>
    </div>
  </div>
);

export const TermsOfService: React.FC = () => (
  <LegalLayout title="Terms of Service">
    <p className="text-sm text-gray-500">Last Updated: April 21, 2026</p>
    
    <h3 className="text-xl font-bold text-white mt-6 mb-2">1. Acceptance of Terms</h3>
    <p>
      By accessing and using RaceLineIQ ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. 
      In addition, when using these particular services, you shall be subject to any posted guidelines or rules applicable to such services.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">2. User Accounts</h3>
    <p>
      To access certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during the registration process 
      and to update such information to keep it accurate, current, and complete. You are responsible for safeguarding your password and for all activities that occur under your account.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">3. User Content</h3>
    <p>
      The Service allows you to upload fitness data files (including but not limited to .gpx, .fit, and .tcx files). You retain all rights in, and are solely responsible for, 
      the content you upload. By uploading content, you grant RaceLineIQ a non-exclusive, worldwide, royalty-free license to use, copy, reproduce, process, adapt, modify, publish, 
      transmit, display, and distribute such content for the purpose of providing and improving the Service.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">4. Prohibited Conduct</h3>
    <p>
      You agree not to use the Service to:
    </p>
    <ul className="list-disc pl-5 space-y-1">
      <li>Upload content that is illegal, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable.</li>
      <li>Impersonate any person or entity.</li>
      <li>Interfere with or disrupt the Service or servers or networks connected to the Service.</li>
    </ul>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">5. Disclaimer of Warranties</h3>
    <p>
      The Service is provided on an "AS IS" and "AS AVAILABLE" basis. RaceLineIQ makes no representations or warranties of any kind, express or implied, as to the operation 
      of their services, or the information, content, or materials included therein.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">6. Limitation of Liability</h3>
    <p>
      In no event shall RaceLineIQ be liable for any direct, indirect, incidental, special, consequential or exemplary damages, including but not limited to, 
      damages for loss of profits, goodwill, use, data or other intangible losses resulting from the use of or inability to use the Service.
    </p>
  </LegalLayout>
);

export const EULA: React.FC = () => (
  <LegalLayout title="End User License Agreement">
    <p className="text-sm text-gray-500">Last Updated: April 21, 2026</p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">1. Grant of License</h3>
    <p>
      RaceLineIQ grants you a revocable, non-exclusive, non-transferable, limited license to download, install (if applicable), and use the Application solely for your personal, 
      non-commercial purposes strictly in accordance with the terms of this Agreement.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">2. Restrictions</h3>
    <p>
      You agree not to, and you will not permit others to:
    </p>
    <ul className="list-disc pl-5 space-y-1">
      <li>License, sell, rent, lease, assign, distribute, transmit, host, outsource, disclose, or otherwise commercially exploit the Application.</li>
      <li>Modify, make derivative works of, disassemble, decrypt, reverse compile or reverse engineer any part of the Application.</li>
      <li>Remove, alter or obscure any proprietary notice (including any notice of copyright or trademark) of RaceLineIQ or its affiliates.</li>
    </ul>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">3. Intellectual Property</h3>
    <p>
      The Application, including without limitation all copyrights, patents, trademarks, trade secrets, and other intellectual property rights are, and shall remain, 
      the sole and exclusive property of RaceLineIQ.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">4. Updates to Application</h3>
    <p>
      RaceLineIQ may from time to time provide enhancements or improvements to the features/functionality of the Application, which may include patches, bug fixes, updates, 
      upgrades and other modifications.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">5. Term and Termination</h3>
    <p>
      This Agreement shall remain in effect until terminated by you or RaceLineIQ. RaceLineIQ may, in its sole discretion, at any time and for any or no reason, suspend or 
      terminate this Agreement with or without prior notice.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">6. Governing Law</h3>
    <p>
      The laws of the jurisdiction in which RaceLineIQ operates, excluding its conflicts of law rules, shall govern this Agreement and your use of the Application.
    </p>
  </LegalLayout>
);

export const PrivacyPolicy: React.FC = () => (
  <LegalLayout title="Privacy Policy">
    <p className="text-sm text-gray-500">Last Updated: October 23, 2026</p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">1. Introduction</h3>
    <p>
      RaceLineIQ ("we", "our", or "us") is dedicated to protecting your privacy and handling your athletic performance data with the utmost care and security. This Privacy Policy outlines how we collect, use, process, disclose, and protect your information when you use our web application, mobile experiences, and integrated device synchronization services (including connections with Garmin Connect, Wahoo, and Hammerhead).
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">2. Information We Collect</h3>
    <p>We collect the following categories of information to provide and improve RaceLineIQ:</p>
    <ul className="list-disc pl-5 space-y-1 mt-2">
      <li><strong>Personal Identification Information:</strong> Name, email address, profile avatar, and athletic profile metrics (such as weight, team affiliation, and cycling license numbers) voluntarily provided during registration.</li>
      <li><strong>Activity & Performance Data:</strong> Comprehensive activity telemetry files (e.g., .FIT, .GPX, .TCX) and synchronized device data including GPS tracks, speed, power output, cadence, heart rate, elevation, timestamps, lap intervals, and device model identifiers. This includes data imported via connected third-party platforms.</li>
      <li><strong>Usage & Diagnostic Data:</strong> Standard logs (timestamps and error diagnostics) are generated to ensure platform stability and troubleshoot errors. We do not use third-party analytics or behavioral tracking services at this time.</li>
    </ul>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">3. How We Use Your Information</h3>
    <p>
      We use the collected information, including data from connected third-party platforms (such as Garmin Connect), for the following purposes:
    </p>
    <ul className="list-disc pl-5 space-y-1 mt-2">
      <li><strong>Service Delivery:</strong> To visualize activity data, calculate performance metrics, and display athlete results.</li>
      <li><strong>AI Analysis:</strong> To provide performance insights and rider feedback, which may include using third-party AI services.</li>
      <li><strong>Community Features:</strong> To display your performance relative to other users. Note that if you upload or link data to a public race event, information such as your name, team, and basic physical attributes, as well as performance metrics (speed, position, heart rate, power), may be visible to other users viewing that event.</li>
      <li><strong>Data Retention & Reliability:</strong> We retain uploaded activity files (.FIT, .GPX, .TCX) and performance data to maintain your historical activity logs and assist with technical error resolution.</li>
      <li><strong>Communication:</strong> To send you administrative information, such as account updates or changes to our terms.</li>
    </ul>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">4. Third Parties & Data Processors</h3>
    <p>
      <strong>We do not sell, rent, monetize, or trade your personal or fitness data with third-party advertisers, data brokers, or marketing networks under any circumstance.</strong>
    </p>
    <p className="mt-2">
      To operate RaceLineIQ, we share data only with verified infrastructure providers and processors bound by confidentiality and data protection agreements:
    </p>
    <ul className="list-disc pl-5 space-y-1 mt-2">
      <li><strong>Google Cloud Platform:</strong> Cloud hosting and secure data storage.</li>
      <li><strong>Google Gemini:</strong> User-requested AI performance analysis.</li>
    </ul>
    <p className="mt-2">
      No other external third parties, advertising networks, or data brokers have access to or process your activity files or device-sourced data.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">5. AI Transparency Statement</h3>
    <p>
      RaceLineIQ offers optional, user-initiated performance summaries powered by Google Gemini:
    </p>
    <ul className="list-disc pl-5 space-y-1 mt-2">
      <li><strong>Explicit User Initiation:</strong> AI analysis is only triggered when you specifically request an AI insight for an event or workout.</li>
      <li><strong>No Model Training:</strong> In accordance with our standards and third-party integration terms, <strong>data and metrics derived from device-sourced data are never used to train, evaluate, fine-tune, or improve artificial intelligence or machine learning models.</strong></li>
      <li><strong>Right to Withdraw / Opt Out:</strong> You may choose not to use AI analysis features at any time. You can disconnect your connected accounts or delete any activity, which removes it from future processing.</li>
    </ul>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">6. Your Data Rights & Withdrawing Consent</h3>
    <p>We provide full transparency and control over your personal and fitness data:</p>
    <ul className="list-disc pl-5 space-y-1 mt-2">
      <li><strong>Access & Correction:</strong> You can view and update your profile information at any time through your account settings.</li>
      <li><strong>Data Export & Deletion:</strong> You may request an export of your data or complete deletion of your account and associated activity files by contacting us at support@racelineiq.com.</li>
      <li><strong>Disconnecting Integrations (Withdrawing Consent):</strong> You can disconnect your third-party integrations at any time from your User Profile settings. Disconnecting immediately revokes RaceLineIQ's connection to that third party, ceasing all incoming data to RaceLineIQ.</li>
    </ul>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">7. Data Security & Integrity</h3>
    <p>
      We enforce industry-standard security practices, including TLS/HTTPS encryption in transit, encrypted storage, secure OAuth 2.0 authentication protocols for third-party device integrations, and strict access controls. API keys and credentials are kept on secure servers and are never exposed in browser.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">8. Children's Privacy</h3>
    <p>
      RaceLineIQ is not designed for or targeted to children under the age of 13. We do not knowingly collect personal information from individuals under 13 years of age. If we become aware that a child under 13 has provided personal data, we will immediately delete that information from our systems.
    </p>

    <h3 className="text-xl font-bold text-white mt-6 mb-2">9. Contact Us</h3>
    <p>
      If you have questions regarding this Privacy Policy, your data, or wish to exercise your data access or deletion rights, please contact us at:
    </p>
    <p className="mt-2 text-blue-400 font-mono">
      support@racelineiq.com
    </p>
  </LegalLayout>
);
